import React, { forwardRef, useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox as MuiCheckbox,
  Menu as MuiMenu,
  MenuItem as MuiMenuItem,
  MenuItemProps,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { IndeterminateCheckBox } from "@mui/icons-material";

import {
  IdsExpandMore as ArrowDropDownIcon,
  IdsExpandLess as ArrowDropUpIcon,
} from "components/shared/svgIcons";
import { Para12Text } from "components/shared/Typography";
import usePreference from "hooks/usePreference";
import { colors } from "theme";
import { PlatformType } from "types/design";

interface IKitsDropdownProps {
  platformType: PlatformType;
}

export const KitsDropdown: React.FC<IKitsDropdownProps> = ({
  platformType,
}) => {
  const { currentKits, setCurrentKits } = usePreference();
  const kits = useMemo(
    () => currentKits.filter((kit) => kit.platformType === platformType),
    [platformType, currentKits]
  );
  const selectedKits = useMemo(
    () => kits.filter((kit) => kit.isActive),
    [kits]
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onClickAll = useCallback(
    (evt: React.MouseEvent) => {
      evt.stopPropagation();

      const nextActiveStatus = selectedKits.length !== kits.length;

      setCurrentKits((kits) => [
        ...kits.map((kit) => ({
          ...kit,
          isActive:
            kit.platformType === platformType ? nextActiveStatus : kit.isActive,
        })),
      ]);
    },
    [kits, platformType, selectedKits, setCurrentKits]
  );

  if (!kits.length) return null;

  return (
    <>
      <Button
        endIcon={
          <Box width={22} height={22} marginRight={-1.25}>
            {Boolean(anchorEl) ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          </Box>
        }
        style={{ textTransform: "none" }}
        onClick={openMenu}
      >
        {`${selectedKits.length ? "Kits" : "None"} (${selectedKits.length}/${
          kits.length
        })`}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        elevation={1}
        MenuListProps={{ disablePadding: true }}
        onClose={() => setAnchorEl(null)}
        onClick={() => setAnchorEl(null)}
      >
        <MenuItem key="all" onClick={onClickAll}>
          <Para12Text>
            <Checkbox
              color="primary"
              indeterminate={
                selectedKits.length > 0 && selectedKits.length !== kits.length
              }
              indeterminateIcon={<IndeterminateCheckBox color="primary" />}
              checked={selectedKits.length === kits.length}
            />
            All kits
          </Para12Text>
        </MenuItem>
        {kits.map((kit) => (
          <MenuItem
            key={kit.kit}
            onClick={(evt: React.MouseEvent) => {
              evt.stopPropagation();
              setCurrentKits([
                ...currentKits.map((k) => {
                  if (kit.kit === k.kit) {
                    return { ...k, isActive: !k.isActive };
                  }
                  return k;
                }),
              ]);
            }}
          >
            <Para12Text>
              <Checkbox color="primary" checked={kit.isActive} />
              {kit.name}
            </Para12Text>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const Menu = styled(MuiMenu)({
  list: {
    border: `1px solid ${colors.grey[500]}`,
    borderRadius: 4,
  },
});

const MenuItem = styled(
  forwardRef<HTMLLIElement, MenuItemProps & { button?: true | undefined }>(
    (props, ref) => <MuiMenuItem ref={ref} dense divider {...props} />
  )
)({
  padding: "4px 16px",
});

const Checkbox = styled(MuiCheckbox)({
  padding: 0,
  marginRight: 8,
});
